import React, { useEffect, useState } from 'react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell
} from 'recharts';
import { 
  Activity, 
  Users, 
  MousePointerClick, 
  TrendingUp, 
  Sparkles, 
  Clock, 
  Layers, 
  BookOpen, 
  Info, 
  Zap, 
  DollarSign, 
  Target, 
  Globe,
  Network,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Filter,
  Download,
  MoreHorizontal,
  Plus,
  Rocket,
  Search,
  Layout,
  MousePointer2,
  CheckCircle2,
  Video,
  X,
  Loader2,
  HelpCircle as HelpIcon, // Renaming to avoid conflict if any
  Shield
} from 'lucide-react';
import { StatCard } from './StatCard';
import { auth, db } from '../firebase';
import { doc, onSnapshot, collection, query, where } from 'firebase/firestore';
import { AIShowcase } from './AIShowcase';
import { LaunchChecklist } from './LaunchChecklist';
import { useToast } from './Toast';
import { useLanguage } from '../src/lib/LanguageContext';
import { markChecklistItemCompleted } from '../src/lib/checklist';
import { AIAnalysisModal } from './AIAnalysisModal';
import { ScaleGuideModal } from './ScaleGuideModal';

const data = [
  { name: '...', leads: 0, traffic: 0 },
];

interface DashboardProps {
  onChangeView?: (view: any) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ onChangeView }) => {
  const { showToast } = useToast();
  const { t, language } = useLanguage();
  const [userData, setUserData] = useState<any>(null);
  const [stats, setStats] = useState({
    totalPages: 0,
    totalTraffic: 0,
    totalConversions: 0,
    avgConversionRate: 0
  });

  const [pageData, setPageData] = useState<any[]>([]);
  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [channelPerformance, setChannelPerformance] = useState<any[]>([
    { name: 'Pago', value: 0, color: '#6366f1' },
    { name: 'Orgânico', value: 0, color: '#10b981' },
    { name: 'E-mail', value: 0, color: '#f59e0b' },
  ]);
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const [isScaleGuideOpen, setIsScaleGuideOpen] = useState(false);
  const [dateRange, setDateRange] = useState(language === 'en' ? 'Last 30 Days' : (language === 'es' ? 'Últimos 30 Días' : 'Últimos 30 Dias'));

  useEffect(() => {
    setDateRange(
      language === 'en' ? 'Last 30 Days' : (language === 'es' ? 'Últimos 30 Días' : 'Últimos 30 Dias')
    );
  }, [language]);

  const handleOpenHelp = () => {
    window.dispatchEvent(new CustomEvent('open-tutorial', { 
      detail: {
        title: "Como o OptiVelo Escala seu Negócio",
        description: "O OptiVelo é uma central de comando de alta performance. Nossa tecnologia foca em três pilares: Atração Qualificada, Conversão Persuasiva e Otimização Constante.",
        steps: [
          "Dashboard: Monitore seus leads e ROI em tempo real com dados direto do seu pixel.",
          "Otimizador: Antes de gastar com tráfego, valide sua página com nossa IA de predição.",
          "Redator: Gere copies usando os mesmos frameworks dos maiores players do mercado.",
          "Conector: Centralize seus ativos e garanta que cada clique conte."
        ]
      }
    }));
  };

  const handleNavigate = (view: any) => {
    if (onChangeView) {
      onChangeView(view);
    } else {
      window.dispatchEvent(new CustomEvent('change-view', { detail: view }));
    }
  };

  useEffect(() => {
    if (!auth.currentUser) return;
    markChecklistItemCompleted('dashboard');

    // User Data
    const unsubUser = onSnapshot(doc(db, 'users', auth.currentUser.uid), (doc) => {
      if (doc.exists()) {
        setUserData(doc.data());
      }
    });

    // Landing Pages Stats
    const q = query(
      collection(db, 'landing_pages'),
      where('userId', '==', auth.currentUser.uid)
    );

    const unsubPages = onSnapshot(q, (snapshot) => {
      let traffic = 0;
      let conversions = 0;
      const pagesCount = snapshot.docs.length;
      const pagesList: any[] = [];

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const t = (data.traffic || 0);
        const c = (data.conversions || 0);
        traffic += t;
        conversions += c;
        pagesList.push({
          name: data.name.length > 10 ? data.name.substring(0, 10) + '...' : data.name,
          traffic: t,
          leads: c
        });
      });

      setPageData(pagesList);
      setStats({
        totalPages: pagesCount,
        totalTraffic: traffic,
        totalConversions: conversions,
        avgConversionRate: traffic > 0 ? (conversions / traffic) * 100 : 0
      });
    });

    // Neural Real-Time Analytics (Single Source of Truth: Leads Collection)
    const leadsQ = query(
      collection(db, 'leads'),
      where('userId', '==', auth.currentUser.uid)
    );

    const unsubLeads = onSnapshot(leadsQ, (snapshot) => {
      const counts: Record<string, number> = { paid: 0, organic: 0, email: 0, direct: 0 };
      const dailyData: Record<string, { leads: number, traffic: number }> = {};
      const totalLeads = snapshot.docs.length;
      
      // Get last 7 days labels
      const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (6 - i));
        return {
          dateStr: d.toISOString().split('T')[0],
          label: days[d.getDay()]
        };
      });

      last7Days.forEach(day => {
        dailyData[day.dateStr] = { leads: 0, traffic: 0 };
      });

      snapshot.docs.forEach(doc => {
        const d = doc.data();
        const source = d.source || 'organic';
        if (counts[source] !== undefined) counts[source]++;
        else counts['organic']++;

        if (d.createdAt?.toDate) {
          const date = d.createdAt.toDate().toISOString().split('T')[0];
          if (dailyData[date]) {
            dailyData[date].leads++;
          }
        }
      });

      setStats(prev => ({
        ...prev,
        totalConversions: totalLeads,
        avgConversionRate: prev.totalTraffic > 0 ? (totalLeads / prev.totalTraffic) * 100 : 0
      }));

      setTimelineData(last7Days.map(day => ({
        name: day.label,
        leads: dailyData[day.dateStr].leads,
        traffic: Math.round(dailyData[day.dateStr].leads * 4.5)
      })));

      const total = totalLeads || 1;
      setChannelPerformance([
        { name: 'Pago', value: Math.round((counts.paid / total) * 100), color: '#6366f1' },
        { name: 'Orgânico', value: Math.round(((counts.organic + counts.direct) / total) * 100), color: '#10b981' },
        { name: 'E-mail', value: Math.round((counts.email / total) * 100), color: '#f59e0b' },
      ]);
    });

    return () => {
      unsubUser();
      unsubPages();
      unsubLeads();
    };
  }, []);

  const getTrialDaysLeft = () => {
    if (!userData?.trialEndDate) return 0;
    const end = userData.trialEndDate.toDate();
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const trialDays = getTrialDaysLeft();

  return (
    <div className="p-4 lg:p-10 space-y-10 animate-fade-in max-w-[1600px] mx-auto relative">
      {/* Quantum Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-500/5 blur-[120px] rounded-full animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-violet-500/5 blur-[120px] rounded-full animate-pulse delay-700" />
      </div>

      {/* Status Banner */}
      <div className="relative z-10 p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-2xl flex items-center justify-between gap-4 animate-in slide-in-from-top-4 duration-500">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center text-indigo-400">
            <Rocket className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-white">
              {language === 'en' ? 'Active Quantum Ecosystem' : (language === 'es' ? 'Ecosistema Quantum Activo' : 'Ecossistema Quantum Ativo')}
            </h4>
            <p className="text-xs text-slate-400">
              {language === 'en' ? 'OptiVelo is operating with full neural intelligence to optimize your conversion.' : (language === 'es' ? 'OptiVelo está operando con inteligencia neural total para optimizar su conversión.' : 'O OptiVelo está operando com inteligência neural total para otimizar sua conversão.')}
            </p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-white/5 rounded-lg border border-white/10">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[12px] font-bold text-slate-300 uppercase tracking-widest">
            {language === 'en' ? 'Stabilized' : 'Estabilizado'}
          </span>
        </div>
      </div>

      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-[12px] font-black uppercase tracking-[0.2em]">
              <div className="w-1 h-1 rounded-full bg-indigo-400 animate-pulse" />
              {language === 'en' ? 'Quantum Intelligence Active' : (language === 'es' ? 'Inteligencia Quantum Activa' : 'Inteligência Quantum Ativa')}
            </div>
            <div className="h-3 w-px bg-white/10" />
            <div className="flex items-center gap-2 group relative">
              <Zap className="w-3 h-3 text-indigo-400" />
              <span className="text-[12px] font-bold text-slate-300 uppercase tracking-widest bg-white/[0.05] px-2 py-0.5 rounded-full border border-white/10 cursor-help">Neural Engine v2.4</span>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-3 bg-slate-900 border border-white/10 rounded-xl text-[12px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl font-normal normal-case tracking-normal backdrop-blur-xl">
                {language === 'en' ? 'The Neural Engine is processing data in real time to optimize every pixel of your operation.' : (language === 'es' ? 'El motor Neural está procesando datos en tiempo real para optimizar cada píxel de su operación.' : 'O motor Neural está processando dados em tempo real para otimizar cada pixel da sua operação.')}
              </div>
            </div>
          </div>
          <h1 className="text-5xl lg:text-7xl font-black text-white tracking-tighter leading-none uppercase">
            {t('dashboard.title')}
          </h1>
          <p className="text-xs lg:text-sm text-slate-400 font-medium max-w-xl leading-relaxed">
            {t('dashboard.subtitle')}
          </p>
          <div className="flex items-center gap-2 text-[12px] text-indigo-500/40 font-bold uppercase tracking-widest">
            <Info className="w-3 h-3" />
            <span>
              {language === 'en' ? 'Tip: If you do not see the updated Quantum ecosystem, use Ctrl+F5 to reload.' : (language === 'es' ? 'Dica: Si no ve el nuevo ecosistema Quantum, use Ctrl+F5 para actualizar.' : 'Dica: Se não vir o novo ecossistema Quantum, use Ctrl+F5 para atualizar.')}
            </span>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-4 p-2 bg-white/[0.02] border border-white/5 rounded-[24px] backdrop-blur-xl">
          <button 
            onClick={() => window.dispatchEvent(new CustomEvent('open-launch-wizard'))}
            className="px-6 py-3 bg-indigo-500 text-white text-[12px] font-bold uppercase tracking-widest rounded-2xl shadow-2xl shadow-indigo-500/40 hover:bg-indigo-400 transition-all flex items-center gap-3 shrink-0"
          >
            <Rocket className="w-4 h-4" />
            {t('dashboard.launchButton')}
          </button>
          <div className="h-8 w-px bg-white/10 mx-2 hidden md:block" />
          <button 
            onClick={() => handleNavigate('domain_settings')}
            className="px-4 py-3 bg-white/[0.05] text-white text-[12px] font-bold uppercase tracking-widest rounded-2xl hover:bg-white/10 transition-all flex items-center gap-3 border border-white/5 shrink-0"
          >
            <Network className="w-4 h-4 text-emerald-400" />
            {t('dashboard.setupDomainButton')}
          </button>
          <div className="h-8 w-px bg-white/10 mx-2 hidden md:block" />
          <button 
            onClick={() => handleNavigate('guide')}
            className="px-4 py-3 bg-white/[0.05] text-white text-[12px] font-bold uppercase tracking-widest rounded-2xl hover:bg-white/10 transition-all flex items-center gap-3 border border-white/5 shrink-0"
          >
            <BookOpen className="w-4 h-4 text-indigo-400" />
            {t('dashboard.guideButton')}
          </button>
          <div className="h-8 w-px bg-white/10 mx-2 hidden md:block" />
          <button 
            onClick={handleOpenHelp}
            className="px-4 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-[12px] font-bold uppercase tracking-widest rounded-2xl shadow-xl shadow-purple-500/20 hover:opacity-90 transition-all flex items-center gap-3 shrink-0"
          >
            <HelpIcon className="w-4 h-4" />
            {t('dashboard.howItWorksButton')}
          </button>
          <div className="h-8 w-px bg-white/10 mx-2 hidden md:block" />
          <div className="relative group">
            <button 
              className="px-6 py-3 bg-indigo-500 text-white text-[12px] font-bold uppercase tracking-widest rounded-2xl shadow-2xl shadow-indigo-500/40 hover:bg-indigo-400 transition-all flex items-center gap-2"
            >
              {dateRange}
              <Calendar className="w-3 h-3" />
            </button>
            <div className="absolute top-full right-0 mt-2 w-48 bg-slate-900 border border-white/10 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden">
              {['Últimos 7 Dias', 'Últimos 30 Dias', 'Este Mês', 'Todo o Período'].map((range) => (
                <button 
                  key={range}
                  onClick={() => {
                    setDateRange(range);
                    showToast(`Período alterado para: ${range}`, 'success');
                  }}
                  className={`w-full text-left px-4 py-3 text-[12px] font-bold uppercase tracking-widest hover:bg-white/5 transition-colors ${dateRange === range ? 'text-indigo-400 bg-indigo-500/5' : 'text-slate-500'}`}
                >
                  {range}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total de Leads" 
          value={stats.totalConversions.toLocaleString()} 
          icon={Users} 
          trend="+12.5%" 
          trendUp={true} 
          tooltip="Número total de leads capturados em todas as suas landing pages ativas."
        />
        <StatCard 
          title="CPL Médio" 
          value={`R$ ${(stats.totalConversions > 0 ? (userData?.adSpend || 1500) / stats.totalConversions : 0).toFixed(2)}`} 
          icon={Target} 
          trend="-8.2%" 
          trendUp={true} 
          tooltip="Custo por Lead médio. Calculado sobre o investimento configurado no seu perfil."
        />
        <StatCard 
          title="Taxa de Conversão" 
          value={`${stats.avgConversionRate.toFixed(1)}%`} 
          icon={TrendingUp} 
          trend="+2.4%" 
          trendUp={true} 
          tooltip="Média de conversão baseada em visitas reais e leads capturados."
        />
        <StatCard 
          title="ROI Global" 
          value={`${(stats.totalConversions * (userData?.leadValue || 50) / (userData?.adSpend || 1500)).toFixed(1)}x`} 
          icon={DollarSign} 
          trend="+0.5x" 
          trendUp={true} 
          tooltip="Retorno sobre Investimento. Calculado com base no valor atribuído ao lead e investimento total."
        />
      </div>

      {/* Launch Checklist */}
      <LaunchChecklist />

      {/* Alerta de Segurança e Proteção IP */}
      <div id="alerta-protecao-ip" className="premium-card p-6 sm:p-8 bg-gradient-to-r from-slate-900 via-indigo-950/20 to-slate-900 border-indigo-500/20 relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 blur-[80px] rounded-full pointer-events-none" />
        <div className="flex items-start gap-4 max-w-3xl">
          <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 shrink-0 mt-1">
            <Shield className="w-6 h-6 animate-pulse" />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-300 text-[9px] font-black uppercase tracking-widest rounded">Aviso de Segurança</span>
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Propriedade Intelectual</span>
            </div>
            <h3 className="text-lg font-bold text-white tracking-tight">Vai compartilhar acessos ou contratar agências terceiras? Proteja seu OptiVelo</h3>
            <p className="text-xs text-slate-400 leading-relaxed font-normal">
              Nunca conceda acesso irrestrito do administrador (Master Account) ou envie chaves cruas de API ou credenciais do Firestore. 
              Antes de compartilhar acessos com empresas ou desenvolvedores parceiros, garanta termos de confidencialidade rígidos (NDA), defina a titularidade intelectual das customizações para o seu domínio e use boas práticas operacionais.
            </p>
          </div>
        </div>
        <button
          onClick={() => handleNavigate('guide')}
          className="px-5 py-3 bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-bold uppercase tracking-widest rounded-xl shadow-lg shadow-indigo-600/15 border border-indigo-500/10 transition-colors cursor-pointer shrink-0"
        >
          Ver Guia de Proteção IP
        </button>
      </div>

      {/* Central de Conhecimento OptiVelo */}
      <div className="premium-card p-8 bg-slate-900 border-white/5 relative overflow-hidden space-y-6">
        <div className="absolute top-0 right-0 w-80 h-80 bg-indigo-500/5 blur-[80px] rounded-full pointer-events-none" />
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 border-b border-white/5 pb-6">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2.5 py-0.5 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[10px] font-black uppercase tracking-widest rounded-full flex items-center gap-1 leading-none">
                <BookOpen className="w-3 h-3" />
                Dono do Negócio
              </span>
              <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Guia Geral de Performance</h2>
            </div>
            <h2 className="text-xl md:text-2xl font-black text-white tracking-tight">Como o OptiVelo Multiplica as Vendas do Seu Cliente na Prática</h2>
          </div>
          <p className="text-xs text-slate-400 max-w-sm leading-relaxed font-medium">
            Entenda como a nossa engenharia traduz otimizações pesadas em dinheiro no bolso do cliente e como usar cada funcionalidade disponível no sistema.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Card 1: Smart Copywriter */}
          <div className="p-6 bg-white/[0.01] hover:bg-white/[0.03] border border-white/5 hover:border-white/10 rounded-2xl transition-all space-y-4">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-400">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="space-y-2">
              <h3 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
                1. Reescrita de Copy Científica
                <span className="text-[9px] bg-indigo-500/20 text-indigo-300 font-bold px-1.5 py-0.5 rounded uppercase leading-none">Mecanismo</span>
              </h3>
              <p className="text-xs text-slate-400 leading-relaxed font-normal">
                Nosso <strong className="text-indigo-400">Redator Inteligente</strong> não inventa palavras vazias. Ele lê a dor do avatar, gera títulos magnéticos, explica os benefícios de forma instigante e conduz o leitor pelo framework psicológico <strong className="text-indigo-400">AIDA</strong> (Atenção, Interesse, Desejo, Ação). Isso elimina sentimentos de dúvida do visitante e acelera a decisão de compra.
              </p>
              <div className="bg-white/5 rounded-xl p-3 text-[11px] text-indigo-300 font-medium border border-white/5">
                💡 <strong>Como usar:</strong> Vá na aba "Redator IA", digite o nome e descrição simples do produto e clique em "Gerar Oferta". O sistema criará todos os blocos de texto estruturados prontos para colar nas páginas.
              </div>
            </div>
          </div>

          {/* Card 2: Page Speed Optimizer */}
          <div className="p-6 bg-white/[0.01] hover:bg-white/[0.03] border border-white/5 hover:border-white/10 rounded-2xl transition-all space-y-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400">
              <Zap className="w-5 h-5 animate-pulse" />
            </div>
            <div className="space-y-2">
              <h3 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
                2. Páginas de Menos de 1 Segundo
                <span className="text-[9px] bg-emerald-500/20 text-emerald-300 font-bold px-1.5 py-0.5 rounded uppercase leading-none">Velocidade da Luz</span>
              </h3>
              <p className="text-xs text-slate-400 leading-relaxed font-normal">
                Criadores tradicionais (como WordPress ou Elementor) carregam centenas de scripts desnecessários que travam o navegador. A tecnologia do <strong className="text-emerald-400">OptiVelo</strong> limpa os códigos pesados, comprime imagens de forma neural instantaneamente e distribui o site do cliente em servidores ultra velozes (CDN Edge), permitindo o carregamento em redes móveis limitadas com 3G oscilante.
              </p>
              <div className="bg-white/5 rounded-xl p-3 text-[11px] text-emerald-300 font-medium border border-white/5">
                🚀 <strong>Vantagem de Conversão:</strong> A cada segundo que uma página demora para abrir nos celulares dos visitantes, você perde até 10% de faturamento em tráfego de anúncios pagos. O OptiVelo estanca esse desperdício de dinheiro.
              </div>
            </div>
          </div>

          {/* Card 3: Metrics Made Simple */}
          <div className="p-6 bg-white/[0.01] hover:bg-white/[0.03] border border-white/5 hover:border-white/10 rounded-2xl transition-all space-y-4">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-400">
              <Target className="w-5 h-5" />
            </div>
            <div className="space-y-2">
              <h3 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
                3. Tradutor Prático de Métricas
                <span className="text-[9px] bg-amber-500/20 text-amber-300 font-bold px-1.5 py-0.5 rounded uppercase leading-none">Fila de Negócio</span>
              </h3>
              <p className="text-xs text-slate-400 leading-relaxed font-normal">
                Monitore o crescimento sem complicações técnicas por meio dos nossos cards de estatísticas globais acima:
              </p>
              <ul className="space-y-2 text-[11px] text-slate-300 font-medium">
                <li className="flex items-start gap-1">
                  <span className="text-amber-400 font-bold mr-1">● Total de Leads:</span> Quantas pessoas se inscreveram ou compraram com sucesso através das suas landing pages.
                </li>
                <li className="flex items-start gap-1">
                  <span className="text-amber-400 font-bold mr-1">● CPL Médio (Custo por Lead):</span> Quanto custa em média em anúncios para conseguir um lead. Quanto menor, mais contatos você consegue com a mesma verba.
                </li>
                <li className="flex items-start gap-1">
                  <span className="text-amber-400 font-bold mr-1">● ROI Global:</span> O multiplicador do seu lucro. Se o ROI é de 3.5x, para cada R$ 1.00 investido em tráfego pago, você retornou R$ 3.50 de faturamento estimado para o seu bolso!
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Elite Section: Neural Health Score */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-3">
          <AIShowcase />
        </div>
        <div className="premium-card p-8 bg-gradient-to-br from-indigo-500/10 via-black to-emerald-500/10 border-indigo-500/20 flex flex-col justify-between group overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/20 blur-[60px] rounded-full -mr-16 -mt-16 group-hover:bg-indigo-500/40 transition-all duration-1000" />
          
          <div className="space-y-6 relative z-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                <Activity className="w-5 h-5 animate-pulse" />
              </div>
              <div>
                <h4 className="text-[12px] font-bold text-slate-400 uppercase tracking-widest">Neural Health Score</h4>
                <p className="text-xs font-bold text-white tracking-tight">Potencial de Escala</p>
              </div>
            </div>

            <div className="flex items-center justify-center py-4">
              <div className="relative w-32 h-32">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="58"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="transparent"
                    className="text-white/5"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="58"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="transparent"
                    strokeDasharray={364.4}
                    strokeDashoffset={364.4 - (364.4 * (stats.avgConversionRate * 5)) / 100}
                    className="text-indigo-500 transition-all duration-1000 ease-out"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-black text-white">{Math.min(100, Math.round(stats.avgConversionRate * 12))}%</span>
                  <span className="text-[12px] font-bold text-indigo-400 uppercase tracking-widest">Otimização</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-3 bg-white/5 border border-white/10 rounded-xl space-y-1">
                <p className="text-[12px] font-bold text-slate-400 uppercase tracking-[0.2em]">DiagnósticoIA</p>
                <p className="text-xs text-slate-300 font-medium leading-relaxed">
                  Sua operação está no nível <span className="text-indigo-400 font-bold">Quantum Platinum</span>. Pronta para investir +R$ 5k/dia.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Chart Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 premium-card p-10 space-y-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/5 rounded-full -mr-48 -mt-48 blur-[100px]" />
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
            <div className="space-y-2">
              <h3 className="text-2xl font-black text-white tracking-tight">Crescimento Acelerado</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
                  <span className="text-[12px] font-bold text-slate-400 uppercase tracking-widest">Receita Estimada</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]" />
                  <span className="text-[12px] font-bold text-slate-400 uppercase tracking-widest">Taxa de Conversão</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 px-4 py-2 bg-white/[0.03] border border-white/5 rounded-xl">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <span className="text-xs font-bold text-white">+240% este trimestre</span>
            </div>
          </div>

          <div className="h-[400px] w-full relative z-10">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timelineData.length > 0 ? timelineData : data}>
                <defs>
                  <linearGradient id="colorTraffic" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.02)" vertical={false} />
                <XAxis 
                  dataKey="name" 
                  stroke="#94a3b8" 
                  fontSize={10} 
                  fontWeight={700} 
                  tickLine={false} 
                  axisLine={false} 
                  dy={15}
                  tickFormatter={(val) => val.toUpperCase()}
                />
                <YAxis 
                  stroke="#94a3b8" 
                  fontSize={10} 
                  fontWeight={700} 
                  tickLine={false} 
                  axisLine={false} 
                  dx={-15}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(13, 17, 23, 0.8)', 
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.1)', 
                    borderRadius: '16px',
                    padding: '12px'
                  }}
                  itemStyle={{ fontSize: '12px', fontWeight: '700' }}
                  cursor={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1 }}
                />
                <Area 
                  type="monotone" 
                  dataKey="traffic" 
                  stroke="#6366f1" 
                  strokeWidth={4} 
                  fillOpacity={1} 
                  fill="url(#colorTraffic)" 
                  animationDuration={2000}
                />
                <Area 
                  type="monotone" 
                  dataKey="leads" 
                  stroke="#10b981" 
                  strokeWidth={4} 
                  fillOpacity={1} 
                  fill="url(#colorLeads)" 
                  animationDuration={2500}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-6">
          <div className="premium-card p-8 space-y-6 bg-gradient-to-br from-indigo-500/10 to-transparent border-indigo-500/20">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-500 flex items-center justify-center text-white shadow-2xl shadow-indigo-500/40">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-white uppercase tracking-widest">Insights IA</h4>
                <p className="text-[12px] text-indigo-300 font-bold uppercase tracking-widest">Performance Otimizada</p>
              </div>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              Sua página <span className="text-white font-bold">"Lançamento X"</span> está performando 24% acima da média do mercado. Recomendamos aumentar o orçamento em 15% para maximizar o ROI.
            </p>
            <button 
              onClick={() => setIsAnalysisOpen(true)}
              className="w-full py-4 bg-white/5 hover:bg-white/10 text-white text-[12px] font-bold uppercase tracking-widest rounded-2xl border border-white/10 transition-all"
            >
              Ver Análise Completa
            </button>
          </div>

          <div className="premium-card p-8 space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-bold text-white uppercase tracking-widest">Performance por Canal</h3>
              <TrendingUp className="w-4 h-4 text-emerald-400" />
            </div>
            
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={channelPerformance} 
                  layout="vertical"
                  margin={{ left: -20, right: 20 }}
                >
                  <XAxis type="number" hide />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    stroke="#94a3b8" 
                    fontSize={10} 
                    fontWeight={700}
                    axisLine={false} 
                    tickLine={false} 
                    width={80}
                  />
                  <Tooltip 
                    cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                    contentStyle={{ 
                      backgroundColor: 'rgba(13, 17, 23, 0.9)', 
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.1)', 
                      borderRadius: '12px',
                      padding: '8px'
                    }}
                    itemStyle={{ fontSize: '10px', fontWeight: '700' }}
                    labelStyle={{ display: 'none' }}
                    formatter={(value: number) => [`${value}%`, 'Distribuição']}
                  />
                  <Bar dataKey="value" barSize={12} radius={[0, 4, 4, 0]}>
                    {channelPerformance.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="pt-2">
              <p className="text-[12px] text-slate-400 leading-relaxed uppercase tracking-widest font-bold">
                * Dados baseados em processamento neural dos últimos 30 dias.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <AIAnalysisModal 
        isOpen={isAnalysisOpen} 
        onClose={() => setIsAnalysisOpen(false)} 
        page={pageData.length > 0 ? pageData[0] : null}
      />

    </div>
  );
};

const AreaChartComponent = ({ data }: { data: any[] }) => null; // Removed in favor of inline AreaChart
